/**
 * Single source of truth for per-restaurant chart colours.
 *
 * Colours are assigned deterministically from a restaurant's stable `id` (not from its
 * position in a list), so the SAME restaurant always gets the SAME colour across every page
 * (dashboard revenue/cost split, revenue trend graph, filter dropdown, …) regardless of how
 * the list is ordered, filtered, or paginated on that page.
 *
 * ⚠️ MIRRORED server-side by `server/domains/reports/shared/restaurantColors.ts`, which
 * colours the venues in the group report PDFs. The hash AND the palette order must stay
 * byte-identical on both sides — change one, change the other in the same commit.
 */

// Stable per-restaurant colour palette (shared by every multi-restaurant chart). Order is part
// of the contract with the server mirror above — appending is safe, reordering recolours venues.
export const RESTAURANT_COLORS = [
  '#13A167',
  '#E8B04B',
  '#8C5A32',
  '#C0453B',
  '#2E8FA3',
  '#C98F2E',
  '#6B8E4E',
  '#E0655A',
  '#7C8894',
  '#3F6C8C',
];

// Deterministic string hash → index. Same id always maps to the same palette slot.
function hashToIndex(str: string, modulo: number): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % modulo;
}

/**
 * The stable colour for a restaurant, keyed by its id. Consistent across all pages.
 * With more restaurants than palette entries some colours repeat, but a given restaurant's
 * colour never changes between renders or pages.
 */
export function getRestaurantColor(restaurantId: string): string {
  if (!restaurantId) return RESTAURANT_COLORS[0];
  return RESTAURANT_COLORS[hashToIndex(restaurantId, RESTAURANT_COLORS.length)];
}
